import { useClientTheme } from "@/app/components/ThemeContext"
import type { InventoryRow } from "@/app/components/InventoryTable"

interface Props {
  sectionNumber: number
  holding: InventoryRow | null
  totalOrri: number | null // summed orri for this STR, null if none
  onClick?: () => void
}

export function SectionCard({ sectionNumber, holding, totalOrri, onClick }: Props) {
  const { theme } = useClientTheme()

  if (!holding) {
    return (
      <div
        style={{
          border: `1px solid ${theme.borderSubtle}`,
          borderRadius: "8px",
          padding: "10px",
          backgroundColor: theme.bgSurface,
          minHeight: "96px",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "11px",
            fontWeight: 600,
            color: theme.textDim,
          }}
        >
          {sectionNumber}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "10px",
            color: theme.textDim,
            marginTop: "6px",
          }}
        >
          No Holdings
        </div>
      </div>
    )
  }

  return (
    <div
      onClick={onClick}
      style={{
        border: `1px solid ${theme.borderColor}`,
        borderRadius: "8px",
        backgroundColor: theme.bgSurface,
        cursor: "pointer",
        overflow: "hidden",
        transition: "border-color 120ms ease, background-color 120ms ease",
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.borderColor = theme.brandPrimary + "60"
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor = theme.bgSurface2
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.borderColor = theme.borderColor
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor = theme.bgSurface
      }}
    >
      {/* Accent top bar */}
      <div style={{ height: "3px", backgroundColor: theme.brandPrimary }} />

      <div style={{ padding: "10px" }}>
        {/* Section number */}
        <div
          style={{
            fontFamily: "var(--font-data)",
            fontSize: "12px",
            fontWeight: 700,
            color: theme.brandPrimary,
            marginBottom: "7px",
          }}
        >
          {sectionNumber}
        </div>

        <div className="flex flex-col gap-1">
          {/* Net Acres */}
          <div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "9px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Net Acres
            </div>
            <div
              style={{
                fontFamily: "var(--font-data)",
                fontSize: "13px",
                fontWeight: 600,
                color: theme.brandTeal,
              }}
            >
              {Number(holding.net_acres).toFixed(2)}
            </div>
          </div>

          {/* NRI */}
          <div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "9px",
                fontWeight: 600,
                color: theme.textDim,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              NRI
            </div>
            <div
              style={{ fontFamily: "var(--font-data)", fontSize: "11px", color: theme.textPrimary }}
            >
              {(Number(holding.weighted_nri) * 100).toFixed(4)}%
            </div>
          </div>

          {/* ORRI — only shown when present */}
          {totalOrri !== null && (
            <div>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "9px",
                  fontWeight: 600,
                  color: theme.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                ORRI
              </div>
              <div
                style={{
                  fontFamily: "var(--font-data)",
                  fontSize: "11px",
                  color: theme.brandAccent,
                }}
              >
                {(totalOrri * 100).toFixed(4)}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
